// RITA-1599 — Prüfung einer Vertragsvorlage auf typische Fehler (Büro-Simulation, Funde 52, 54, 55, 63).
//
// Der Vorlagen-Editor zeigt die Ergebnisse als gelben Kasten unter der Zuordnung und fragt beim Speichern nach; er
// sperrt nichts. Geprüft wird das ganze Dokument (Kopfzeile, Text, Fußzeile) in fester Reihenfolge der fünf Fälle:
// 1. Ein Vertrag (Art „Vertrag“) enthält kein {{VERTRAGSPARTNER}}.
// 2. {{ANREDE}} steht direkt vor {{VERTRAGSPARTNER}} bzw. {{VERTRAGSINHABER}}: dazwischen höchstens Leerzeichen in
//    derselben Zeile (ein Zeilenumbruch, ein Komma oder anderer Text trennt die beiden).
// 3. Eine Vorlage „Firma als Vertragspartner“ enthält {{ANREDE}} bzw. {{PERSOENLICHE_ANREDE}}.
// 4. Getippter Text der Form „{{…}}“ ist kein Platzhalter und wird nicht ersetzt (nur melden, nicht umwandeln –
//    Entscheidung Julia 30.09.2026). Benachbarte Textläufe zählen zusammen, auch wenn ein Teil fett ist. Platzhalter,
//    die es nicht mehr gibt, nennt schon der eigene Hinweis beim Öffnen; sie stehen hier nicht noch einmal.
// 5. Eine Vorlage „Berater“ enthält das Wort „Beraterin“, eine Vorlage „Beraterin“ das Wort „Berater“ – nur ganze
//    Wörter, auch in Großbuchstaben; „Berater/in“ (auch „*in“, „:in“, „_in“, „(in)“) ist die neutrale Form und zählt
//    nicht. Bei „Berater/in – automatisch“ und „Firma als Vertragspartner“ gibt es diese Prüfung nicht.
// Jeder Fall mit Fundstellen nennt sie als Auswahl im Dokument („Stelle zeigen“); Fall 1 hat keine Stelle.
import { VorlagenZuordnungForm } from '../../services/vorlagenZuordnung';
import {
  ANREDE_BEI_FIRMA_TEXT,
  ANREDE_VOR_VERTRAGSINHABER_TEXT,
  ANREDE_VOR_VERTRAGSPARTNER_TEXT,
  falschesGeschlechtText,
  getippterPlatzhalterText,
  PERSOENLICHE_ANREDE_BEI_FIRMA_TEXT,
  VERTRAGSPARTNER_FEHLT_TEXT,
} from '../../services/vorlagenhinweiseTexte';
import { anzahlEintraege, Auswahl, Bereich, BEREICHE, bloeckeVon, inhaltVon, position } from './positionen';
import { Inlineknoten, Vertragsdokument } from './types';
import type { VertragPlatzhalterKey } from './platzhalterkatalog';

export type Hinweisart =
  | 'vertragspartnerFehlt'
  | 'anredeVorVertragspartner'
  | 'anredeVorVertragsinhaber'
  | 'anredeBeiFirma'
  | 'persoenlicheAnredeBeiFirma'
  | 'getippterPlatzhalter'
  | 'falschesGeschlecht';

export interface Vorlagenhinweis {
  /** Eindeutig je Hinweis (Art, beim getippten Platzhalter mit dem Text). */
  id: string;
  art: Hinweisart;
  text: string;
  /** Fundstellen in Dokumentreihenfolge (Kopfzeile, Text, Fußzeile); leer, wenn es keine Stelle gibt. */
  stellen: Auswahl[];
}

/** Eine Zeile des Dokuments: Absatz oder Listeneintrag eines Bereichs. */
interface Zeile {
  bereich: Bereich;
  blockId: string;
  eintrag: number;
  inhalt: Inlineknoten[];
}

function zeilen(dokument: Vertragsdokument): Zeile[] {
  return BEREICHE.flatMap((bereich) =>
    bloeckeVon(dokument, bereich).flatMap((block) =>
      Array.from({ length: anzahlEintraege(block) }, (_, eintrag) => ({
        bereich,
        blockId: block.id,
        eintrag,
        inhalt: inhaltVon(block, eintrag) ?? [],
      })),
    ),
  );
}

function auswahlIn(zeile: Zeile, von: number, bis: number): Auswahl {
  return {
    anker: position(zeile.bereich, zeile.blockId, von, zeile.eintrag),
    fokus: position(zeile.bereich, zeile.blockId, bis, zeile.eintrag),
  };
}

interface Platzhalterstelle {
  zeile: Zeile;
  index: number;
  offset: number;
  schluessel: VertragPlatzhalterKey;
}

/** Zusammenhängender Text aus benachbarten Textläufen und sein Anfang in Einheiten der Zeile. */
interface Textstueck {
  zeile: Zeile;
  offset: number;
  text: string;
}

function zerlegen(dokument: Vertragsdokument) {
  const platzhalter: Platzhalterstelle[] = [];
  const texte: Textstueck[] = [];
  zeilen(dokument).forEach((zeile) => {
    let offset = 0;
    let stueck: Textstueck | null = null;
    zeile.inhalt.forEach((knoten, index) => {
      if (knoten.typ === 'text') {
        if (stueck) stueck.text += knoten.text;
        else {
          stueck = { zeile, offset, text: knoten.text };
          texte.push(stueck);
        }
        offset += knoten.text.length;
        return;
      }
      stueck = null;
      if (knoten.typ === 'platzhalter') platzhalter.push({ zeile, index, offset, schluessel: knoten.schluessel });
      offset += 1;
    });
  });
  return { platzhalter, texte };
}

const NUR_LEERRAUM = /^\s*$/;

/** Der nächste Platzhalter hinter `index` in derselben Zeile, wenn bis dahin nur Leerzeichen stehen. */
function direktDahinter(zeile: Zeile, index: number): VertragPlatzhalterKey | null {
  for (let i = index + 1; i < zeile.inhalt.length; i += 1) {
    const knoten = zeile.inhalt[i];
    if (knoten.typ === 'platzhalter') return knoten.schluessel;
    if (knoten.typ !== 'text' || !NUR_LEERRAUM.test(knoten.text)) return null;
  }
  return null;
}

const GETIPPT = /\{\{[^{}]+\}\}/g;
const WORTZEICHEN = /[\p{L}\p{N}_]/u;
/** Neutrale Schreibweisen direkt hinter „Berater“: „/in“, „/-in“, „*in“, „:in“, „_in“, „(in)“. */
const NEUTRALE_ENDUNG = /^(?:[/*:_]-?in|\(in\))/i;

function wortStellen(stueck: Textstueck, woerter: readonly string[], neutralAuslassen: boolean): Auswahl[] {
  const stellen: Auswahl[] = [];
  woerter.forEach((wort) => {
    let ab = stueck.text.indexOf(wort);
    while (ab >= 0) {
      const ende = ab + wort.length;
      const davor = ab > 0 ? stueck.text[ab - 1] : '';
      const dahinter = stueck.text[ende] ?? '';
      const ganzesWort = !WORTZEICHEN.test(davor) && !WORTZEICHEN.test(dahinter);
      const neutral = neutralAuslassen && NEUTRALE_ENDUNG.test(stueck.text.slice(ende));
      if (ganzesWort && !neutral) stellen.push(auswahlIn(stueck.zeile, stueck.offset + ab, stueck.offset + ende));
      ab = stueck.text.indexOf(wort, ab + 1);
    }
  });
  return stellen;
}

/** Ordnet Stellen nach Bereich (Kopf, Text, Fuß), Zeile und Offset – so springt „Stelle zeigen“ der Reihe nach. */
function sortieren(dokument: Vertragsdokument, stellen: Auswahl[]): Auswahl[] {
  const reihenfolge = new Map<string, number>();
  zeilen(dokument).forEach((zeile, nummer) =>
    reihenfolge.set(`${zeile.bereich}\u0000${zeile.blockId}\u0000${zeile.eintrag}`, nummer),
  );
  const rang = (auswahl: Auswahl) =>
    reihenfolge.get(`${auswahl.anker.bereich}\u0000${auswahl.anker.blockId}\u0000${auswahl.anker.eintrag}`) ?? 0;
  return [...stellen].sort((a, b) => rang(a) - rang(b) || a.anker.offset - b.anker.offset);
}

/**
 * Hinweise zu einer Vorlage. `zuordnung` liefert Art und „Für“; `bekanntAlsAlt` sind die Namen der Platzhalter, die
 * es nicht mehr gibt (beim Öffnen gemeldet) – sie zählen nicht als getippt.
 */
export function pruefeVorlage(
  dokument: Vertragsdokument,
  zuordnung: Pick<VorlagenZuordnungForm, 'art' | 'fuer'>,
  bekanntAlsAlt: readonly string[] = [],
): Vorlagenhinweis[] {
  const { platzhalter, texte } = zerlegen(dokument);
  const hinweise: Vorlagenhinweis[] = [];
  const hinzu = (art: Hinweisart, text: string, stellen: Auswahl[], id: string = art) =>
    hinweise.push({ id, art, text, stellen });
  const anredeStelle = (stelle: Platzhalterstelle) => auswahlIn(stelle.zeile, stelle.offset, stelle.offset + 1);

  // 1. Vertrag ohne Vertragspartner
  if (zuordnung.art === 'contract' && !platzhalter.some((stelle) => stelle.schluessel === 'VERTRAGSPARTNER')) {
    hinzu('vertragspartnerFehlt', VERTRAGSPARTNER_FEHLT_TEXT, []);
  }

  // 2. Anrede direkt vor Vertragspartner bzw. Vertragsinhaber
  const anreden = platzhalter.filter((stelle) => stelle.schluessel === 'ANREDE');
  const vorPartner = anreden.filter((stelle) => direktDahinter(stelle.zeile, stelle.index) === 'VERTRAGSPARTNER');
  const vorInhaber = anreden.filter((stelle) => direktDahinter(stelle.zeile, stelle.index) === 'VERTRAGSINHABER');
  if (vorPartner.length > 0) {
    hinzu('anredeVorVertragspartner', ANREDE_VOR_VERTRAGSPARTNER_TEXT, vorPartner.map(anredeStelle));
  }
  if (vorInhaber.length > 0) {
    hinzu('anredeVorVertragsinhaber', ANREDE_VOR_VERTRAGSINHABER_TEXT, vorInhaber.map(anredeStelle));
  }

  // 3. Anrede in einer Vorlage für eine Firma als Vertragspartner
  if (zuordnung.fuer === 'company') {
    if (anreden.length > 0) hinzu('anredeBeiFirma', ANREDE_BEI_FIRMA_TEXT, anreden.map(anredeStelle));
    const persoenlich = platzhalter.filter((stelle) => stelle.schluessel === 'PERSOENLICHE_ANREDE');
    if (persoenlich.length > 0) {
      hinzu('persoenlicheAnredeBeiFirma', PERSOENLICHE_ANREDE_BEI_FIRMA_TEXT, persoenlich.map(anredeStelle));
    }
  }

  // 4. Getippte Platzhalter: je verschiedenem Text ein Hinweis mit allen Stellen
  const getippt = new Map<string, Auswahl[]>();
  texte.forEach((stueck) => {
    Array.from(stueck.text.matchAll(GETIPPT)).forEach((treffer) => {
      const text = treffer[0];
      if (bekanntAlsAlt.includes(text.slice(2, -2).trim())) return;
      const von = stueck.offset + (treffer.index ?? 0);
      getippt.set(text, [...(getippt.get(text) ?? []), auswahlIn(stueck.zeile, von, von + text.length)]);
    });
  });
  getippt.forEach((stellen, text) =>
    hinzu('getippterPlatzhalter', getippterPlatzhalterText(text), stellen, `getippterPlatzhalter:${text}`),
  );

  // 5. Wort „Beraterin“ in einer Vorlage für „Berater“ bzw. umgekehrt
  if (zuordnung.fuer === 'male' || zuordnung.fuer === 'female') {
    const woerter = zuordnung.fuer === 'male' ? ['Beraterin', 'BERATERIN'] : ['Berater', 'BERATER'];
    const stellen = sortieren(
      dokument,
      texte.flatMap((stueck) => wortStellen(stueck, woerter, zuordnung.fuer === 'female')),
    );
    if (stellen.length > 0) {
      hinzu('falschesGeschlecht', falschesGeschlechtText(zuordnung.fuer, stellen.length), stellen);
    }
  }
  return hinweise;
}
